import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useForm, type Path } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { publicApplications, type AlreadyAppliedCheck, type PublicApplicationRequest } from "@/features/applications/api/applications.api";
import { toApiDateTime } from "@/utils/dates";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FormFieldSize, FormRequiredCue } from "@/components/form";
import { Loading } from "@/components/Loading";
import { useConfirm } from "@/components/ConfirmDialog";
import { loadFormDraft, useFormDraftAutosave } from "@/hooks/useFormDraftAutosave";
import {
  Progress, StickyActions, useWizardSteps, useShareLink, fileBase64, clean, num,
  ConversionErrorBanner, failedDocSlots, requiredDocSlots, DOC_SLOT_ORDER, type DocSlot,
  DraftSaveIndicator, DraftRestoredNotice, onboardingDraftKey,
  DRAFT_SENSITIVE_FIELDS, type DraftFiles, canonicalOwnershipType, canonicalOwnerTitle,
} from "@/features/onboarding/components/wizard";
import {
  STEP_KEYS, makeSchema, type FormState, STEP_FIELDS, EMPTY_FORM,
  WelcomeStep, LegalStep, BusinessStep, OwnerStep, BankStep,
} from "@/features/onboarding/components/apply";
import { LanguageMenu, LanguageProvider, useLang, useT, type Translate } from "@/features/onboarding/i18n";

const DL_STEP = 3;
const CHECK_STEP = 4;

function conversionText(t: Translate, slots: DocSlot[]): string | null {
  const names = DOC_SLOT_ORDER.filter((s) => slots.includes(s)).map((s) => t(s === "driverLicense" ? "conv.dl" : "conv.check"));
  if (names.length === 0) return null;
  const joined = names.length === 1 ? names[0] : t("conv.pair", { a: names[0], b: names[1] });
  return t("conv.message", { names: joined });
}

function previousNote(t: Translate, slot: DocSlot, name: string | undefined): string | null {
  if (!name) return null;
  const lead = t("file.previousLead", { name });
  return `${lead} ${t(slot === "driverLicense" ? "file.previousDl" : "file.previousCheck")}`;
}

export default function PublicApplyPage() {
  return (
    <LanguageProvider>
      <ApplyPage />
    </LanguageProvider>
  );
}

function ApplyPage() {
  const { lang, t } = useLang();
  const schema = useMemo(() => makeSchema(t), [t]);
  const steps = useMemo(() => STEP_KEYS.map((k) => t(k)), [t]);
  const { ref, pricedRef } = useParams<{ ref?: string; pricedRef?: string }>();

  const [searchParams] = useSearchParams();

  const pricedCode = pricedRef?.trim() || null;
  const referringRef = pricedCode ? null : ref?.trim() || null;
  const hasReferral = referringRef != null || pricedCode != null;
  const shareLink = useShareLink(
    pricedCode ?? referringRef,
    pricedCode ? "PRICED" : "APPLY",
    pricedCode ? null : searchParams.get("c")?.trim() || null,
  );
  const confirm = useConfirm();
  const draftKey = onboardingDraftKey("apply", pricedCode ? `p-${pricedCode}` : referringRef);
  const [initialDraft] = useState(() => loadFormDraft(draftKey, EMPTY_FORM));
  const [restoredAt, setRestoredAt] = useState<number | null>(initialDraft?.draft.lastEditAt ?? null);
  const [previousFiles, setPreviousFiles] = useState<DraftFiles>(initialDraft?.draft.files ?? {});

  const [error, setError] = useState<string | null>(null);
  const [resultId, setResultId] = useState<string | null>(null);
  const [dlFile, setDlFile] = useState<File | null>(null);
  const [checkFile, setCheckFile] = useState<File | null>(null);
  const [failedDocs, setFailedDocs] = useState<DocSlot[]>([]);
  const [failAttempt, setFailAttempt] = useState(0);
  const [scanning, setScanning] = useState(false);
  const [missingDoc, setMissingDoc] = useState<DocSlot | null>(null);
  const [dup, setDup] = useState<DuplicatePrompt | null>(null);
  const [dupBusy, setDupBusy] = useState(false);
  const [outcome, setOutcome] = useState<"same" | "updated" | null>(null);

  const form = useForm<FormState>({
    resolver: zodResolver(schema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: initialDraft
      ? {
        ...initialDraft.values,
        ownershipType: canonicalOwnershipType(initialDraft.values.ownershipType),
        ownerTitle: canonicalOwnerTitle(initialDraft.values.ownerTitle),
      }
      : EMPTY_FORM,
  });

  useEffect(() => {
    const invalid = Object.keys(form.formState.errors) as Path<FormState>[];
    if (invalid.length > 0) void form.trigger(invalid);
  }, [lang]);

  const wiz = useWizardSteps({
    form,
    stepCount: STEP_KEYS.length,
    stepFields: STEP_FIELDS,
    initialStep: initialDraft?.draft.step,
    initialMaxValidated: initialDraft?.draft.maxValidated,
  });

  const draftFiles = useMemo<DraftFiles>(() => ({
    driverLicense: dlFile?.name ?? previousFiles.driverLicense,
    voidedCheck: checkFile?.name ?? previousFiles.voidedCheck,
  }), [dlFile, checkFile, previousFiles]);

  const autosave = useFormDraftAutosave({
    form,
    storageKey: draftKey,
    enabled: !resultId,
    restored: initialDraft?.draft ?? null,
    step: wiz.step,
    maxValidated: wiz.maxValidated,
    files: draftFiles,
    sensitive: DRAFT_SENSITIVE_FIELDS,
  });

  function attachDl(f: File | null) {
    setDlFile(f);
    setPreviousFiles((p) => ({ ...p, driverLicense: undefined }));
  }
  function attachCheck(f: File | null) {
    setCheckFile(f);
    setPreviousFiles((p) => ({ ...p, voidedCheck: undefined }));
  }

  async function startOver() {
    const ok = await confirm({
      title: t("page.startOverTitle"),
      description: t("page.startOverBody"),
      confirmLabel: t("wiz.startOver"),
      cancelLabel: t("common.cancel"),
      destructive: true,
    });
    if (!ok) return;
    autosave.discard();
    setRestoredAt(null);
    setPreviousFiles({});
    setFailedDocs([]);
    setError(null);
    reset();
  }

  function flagMissingDoc(): DocSlot | null {
    const missing: DocSlot | null = !dlFile
      ? "driverLicense"
      : !checkFile
        ? "voidedCheck"
        : null;
    setMissingDoc(missing);
    return missing;
  }

  async function handleNext() {
    autosave.flush();
    if (wiz.step === DL_STEP && !dlFile) {
      setMissingDoc("driverLicense");
      return;
    }
    setMissingDoc(null);
    if (await wiz.next()) setError(null);
  }
  function handleBack() {
    setError(null);
    wiz.back();
  }
  function handleGoTo(target: number) {
    setError(null);
    wiz.goToStep(target);
  }

  const submit = form.handleSubmit(
    async (v) => {
      const missing = flagMissingDoc();
      if (missing) {
        wiz.setStep(missing === "driverLicense" ? DL_STEP : CHECK_STEP);
        return;
      }
      setError(null);
      setFailedDocs([]);
      try {
        const dba = v.dbaSameAsLegal ? v.legalName.trim() : v.dbaName.trim();
        const payload: PublicApplicationRequest = {
          dbaName: dba,
          legalName: clean(v.legalName),
          mcc: clean(v.mcc),
          contactName: clean(v.contactName),
          email: clean(v.email),
          phone: clean(v.phone),
          addressLine1: clean(v.addressLine1),
          addressLine2: clean(v.addressLine2),
          city: clean(v.city),
          state: clean(v.state),
          zip: clean(v.zip),
          ein: clean(v.ein),
          notes: clean(v.specialNotes),
          agentCode: referringRef,
          pricedLinkCode: pricedCode,
          campaignId: shareLink.campaignId,
          website: clean(v.website),
          ownershipType: clean(v.ownershipType),
          businessStartDate: toApiDateTime(v.businessStartDate),
          dbaSameAsLegal: v.dbaSameAsLegal,
          dbaAddressLine1: clean(v.dbaAddressLine1),
          dbaCity: clean(v.dbaCity),
          dbaState: clean(v.dbaState),
          dbaZip: clean(v.dbaZip),
          averageMonthlyVolume: num(v.averageMonthlyVolume),
          averageTransactionAmount: num(v.averageTransactionAmount),
          maxTransactionAmount: num(v.maxTransactionAmount),
          productDescription: clean(v.productDescription),
          ownerFirstName: clean(v.ownerFirstName),
          ownerMiddleName: clean(v.ownerMiddleName),
          ownerLastName: clean(v.ownerLastName),
          ownerTitle: clean(v.ownerTitle),
          ownerDateOfBirth: toApiDateTime(v.ownerDateOfBirth),
          ownerIsUsCitizen: v.ownerCitizenship === "us",
          ownerSsn: v.ownerCitizenship === "us" ? clean(v.ownerSsn) : null,
          ownerPassportNumber: v.ownerCitizenship === "us" ? null : clean(v.ownerPassportNumber.replace(/\s/g, "").toUpperCase()),
          ownerPassportCountry: v.ownerCitizenship === "us" ? null : clean(v.ownerPassportCountry),
          ownerPhone: clean(v.ownerPhone),
          ownerHomeAddressLine1: clean(v.ownerHomeAddressLine1),
          ownerHomeCity: clean(v.ownerHomeCity),
          ownerHomeState: clean(v.ownerHomeState),
          ownerHomeZip: clean(v.ownerHomeZip),
          bankName: clean(v.bankName),
          depositAccountType: clean(v.depositAccountType),
          ssn: clean(v.ssn),
          bankRoutingNumber: clean(v.bankRoutingNumber),
          bankAccountNumber: clean(v.bankAccountNumber),
          separateWithdrawAccount: v.separateWithdrawAccount,
          withdrawBankName: clean(v.withdrawBankName),
          withdrawAccountType: clean(v.withdrawAccountType),
          withdrawRoutingNumber: v.separateWithdrawAccount ? clean(v.withdrawRoutingNumber) : null,
          withdrawAccountNumber: v.separateWithdrawAccount ? clean(v.withdrawAccountNumber) : null,
          driverLicenseImageBase64: dlFile ? await fileBase64(dlFile) : null,
          driverLicenseFileName: dlFile?.name ?? null,
          driverLicenseContentType: dlFile?.type || null,
          voidedCheckImageBase64: checkFile ? await fileBase64(checkFile) : null,
          voidedCheckFileName: checkFile?.name ?? null,
          voidedCheckContentType: checkFile?.type || null,
        };
        const match = await findEarlier(payload);
        if (match) {
          setDup({ ...match, payload, confirmReplace: false });
          return;
        }
        await finish(payload, false);
      } catch (e2) {
        handleSubmitError(e2);
      }
    },
    (errs) => {
      setError(null);
      flagMissingDoc();
      wiz.goToError(errs);
    },
  );

  function identifiers(payload: PublicApplicationRequest): AlreadyAppliedCheck {
    return { taxId: payload.ein ?? payload.ssn ?? null, email: payload.email ?? null, phone: payload.phone ?? null };
  }

  async function findEarlier(payload: PublicApplicationRequest) {
    try {
      const res = await publicApplications.alreadyApplied(identifiers(payload));
      if (!res.matched) return null;
      return { name: payload.dbaName || payload.legalName || "", date: res.submittedOn ? shortDate(res.submittedOn, lang) : "" };
    } catch {
      return null;
    }
  }

  async function finish(payload: PublicApplicationRequest, replacesExisting: boolean) {
    const res = await publicApplications.submit(payload);
    if (replacesExisting) await publicApplications.replacesEarlier(res.id).catch(() => undefined);
    autosave.discard();
    setRestoredAt(null);
    setPreviousFiles({});
    setOutcome(replacesExisting ? "updated" : null);
    setResultId(res.id);
  }

  async function answerDuplicate(choice: "same" | "fix" | "new" | "replace" | "back" | "close") {
    if (!dup) return;
    if (choice === "fix") return setDup({ ...dup, confirmReplace: true });
    if (choice === "back") return setDup({ ...dup, confirmReplace: false });
    if (choice === "close") return setDup(null);
    setDupBusy(true);
    setError(null);
    try {
      if (choice === "same") {
        await publicApplications.confirmSame(identifiers(dup.payload)).catch(() => undefined);
        autosave.discard();
        setDup(null);
        setOutcome("same");
        return;
      }
      const payload = dup.payload;
      setDup(null);
      await finish(payload, choice === "replace");
    } catch (e2) {
      handleSubmitError(e2);
    } finally {
      setDupBusy(false);
    }
  }

  function handleSubmitError(e2: unknown) {
    const slots = failedDocSlots(e2);
    if (slots.length > 0) {
      setFailedDocs(slots);
      setFailAttempt((n) => n + 1);
      if (slots.includes("driverLicense")) setDlFile(null);
      if (slots.includes("voidedCheck")) setCheckFile(null);
      return;
    }
    const missing = requiredDocSlots(e2);
    if (missing.length > 0) {
      if (missing.includes("driverLicense")) setDlFile(null);
      if (missing.includes("voidedCheck")) setCheckFile(null);
      setMissingDoc(missing[0]);
      wiz.setStep(missing[0] === "driverLicense" ? DL_STEP : CHECK_STEP);
      return;
    }
    setError(e2 instanceof Error ? e2.message : t("page.genericError"));
  }

  function reset() {
    setResultId(null);
    setMissingDoc(null);
    form.reset(EMPTY_FORM);
    wiz.reset();
    setDlFile(null);
    setCheckFile(null);
  }

  const { step, maxValidated } = wiz;
  const isLast = step === STEP_KEYS.length - 1;
  const isWelcome = step === 0;
  const dlError = dlFile
    ? null
    : failedDocs.includes("driverLicense")
      ? t("file.reupload")
      : missingDoc === "driverLicense"
        ? t("page.missingDl")
        : null;
  const checkError = checkFile
    ? null
    : failedDocs.includes("voidedCheck")
      ? t("file.reupload")
      : missingDoc === "voidedCheck"
        ? t("page.missingCheck")
        : null;
  const conversionError = conversionText(t, failedDocs);

  function goToFailedDoc() {
    wiz.setStep(failedDocs.includes("driverLicense") ? DL_STEP : CHECK_STEP);
  }

  if (shareLink.checking) {
    return (
      <Shell hasReferral={hasReferral} agentName={shareLink.agentName}>
        <div className="px-6 py-12 flex justify-center">
          <Loading>{t("page.checkingLink")}</Loading>
        </div>
      </Shell>
    );
  }

  if (shareLink.invalid || shareLink.unverified) {
    return (
      <Shell hasReferral={false} agentName={null}>
        <div className="text-center py-12 px-6">
          <h1 className="text-xl font-semibold text-slate-800">
            {shareLink.unverified ? t("page.linkUnverifiedTitle") : t("page.linkInvalidTitle")}
          </h1>
          <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
            {shareLink.unverified ? t("page.linkUnverifiedBody") : t("page.linkInvalidBody")}
          </p>
        </div>
      </Shell>
    );
  }

  if (outcome) {
    return (
      <Shell hasReferral={hasReferral} agentName={shareLink.agentName}>
        <AlreadyAppliedDone outcome={outcome} />
      </Shell>
    );
  }

  if (resultId) {
    return (
      <Shell hasReferral={hasReferral} agentName={shareLink.agentName}>
        <Confirmation id={resultId} onReset={pricedCode ? undefined : reset} />
      </Shell>
    );
  }

  return (
    <Form {...form}>
      <FormFieldSize value="lg">
      <FormRequiredCue>
      <div className="min-h-screen bg-white sm:bg-slate-50 flex flex-col">
        <Header hasReferral={hasReferral} agentName={shareLink.agentName} />
        <Progress steps={steps} step={step} maxValidated={maxValidated} onStepClick={handleGoTo} disabled={scanning} />

        <main className={"flex-1 sm:px-4 sm:pt-4 " + (isWelcome ? "pb-8" : "pb-32 sm:pb-28")}>
          <div className="max-w-2xl mx-auto">
            {!isWelcome && (
              <div className="px-4 sm:px-0">
                <DraftSaveIndicator status={autosave.status} savedAt={autosave.savedAt} />
              </div>
            )}
            <div className="bg-white px-4 py-5 sm:rounded-2xl sm:border sm:border-slate-200 sm:p-7">
              {restoredAt != null && !isWelcome && (
                <DraftRestoredNotice at={restoredAt} onStartOver={startOver} disabled={scanning} />
              )}
              {conversionError && (
                <ConversionErrorBanner message={conversionError} attempt={failAttempt} onJump={goToFailedDoc} />
              )}
              {step === 0 && (
                <WelcomeStep hasReferral={hasReferral} agentName={shareLink.agentName} onStart={handleNext} />
              )}
              {step === 1 && <LegalStep form={form} />}
              {step === 2 && <BusinessStep form={form} />}
              {step === 3 && (
                <OwnerStep
                  form={form}
                  dlFile={dlFile}
                  onDlFile={attachDl}
                  dlError={dlError}
                  dlPreviousNote={previousNote(t, "driverLicense", previousFiles.driverLicense)}
                  onScanned={autosave.flush}
                  onBusyChange={setScanning}
                />
              )}
              {step === 4 && (
                <BankStep
                  form={form}
                  checkFile={checkFile}
                  onCheckFile={attachCheck}
                  checkError={checkError}
                  checkPreviousNote={previousNote(t, "voidedCheck", previousFiles.voidedCheck)}
                  onScanned={autosave.flush}
                  onBusyChange={setScanning}
                />
              )}
            </div>
          </div>
        </main>

        {error && (
          <div
            className="fixed bottom-[72px] inset-x-0 px-4 pb-2"
            style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 0.5rem)" }}
          >
            <div className="max-w-2xl mx-auto bg-destructive/10 border border-destructive/30 text-destructive text-sm rounded-lg px-4 py-2">
              {error}
            </div>
          </div>
        )}

        <AlreadyAppliedDialog prompt={dup} busy={dupBusy} onAnswer={(c) => void answerDuplicate(c)} />

        {!isWelcome && (
          <StickyActions
            isFirst={step === 0}
            isLast={isLast}
            submitting={form.formState.isSubmitting}
            busy={scanning}
            submitLabel={t("page.submit")}
            onPrev={handleBack}
            onNext={handleNext}
            onSubmit={submit}
          />
        )}
      </div>
      </FormRequiredCue>
      </FormFieldSize>
    </Form>
  );
}

function Shell({
  hasReferral,
  agentName,
  children,
}: {
  hasReferral: boolean;
  agentName: string | null;
  children: ReactNode;
}) {
  return (
    <div className="min-h-screen bg-white sm:bg-slate-50 flex flex-col">
      <Header hasReferral={hasReferral} agentName={agentName} />
      <main className="flex-1 sm:px-4 sm:py-10">
        <div className="max-w-2xl mx-auto bg-white sm:rounded-2xl sm:border sm:border-slate-200">
          {children}
        </div>
      </main>
    </div>
  );
}

function Header({ hasReferral, agentName }: { hasReferral: boolean; agentName: string | null }) {
  const t = useT();
  return (
    <header className="bg-white border-b border-slate-200">
      <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center font-bold text-primary-foreground text-sm">
            BL
          </div>
          <div className="leading-tight">
            <div className="font-semibold text-slate-900 text-sm">BLogic Systems</div>
            <div className="text-[11px] text-slate-500">{t("page.subtitle")}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {hasReferral && (
            <div className="hidden sm:block text-right leading-tight">
              <div className="text-[11px] text-slate-500">{t("page.referredBy")}</div>
              <div className="text-xs font-medium text-slate-700">{agentName ?? t("page.aBlogicAgent")}</div>
            </div>
          )}
          <LanguageMenu />
        </div>
      </div>
    </header>
  );
}

type DuplicatePrompt = {
  name: string;
  date: string;
  payload: PublicApplicationRequest;
  confirmReplace: boolean;
};

function shortDate(iso: string, lang: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(lang === "zh" ? "zh-CN" : lang, { month: "short", day: "numeric", timeZone: "UTC" });
}

function AlreadyAppliedDialog({
  prompt,
  busy,
  onAnswer,
}: {
  prompt: DuplicatePrompt | null;
  busy: boolean;
  onAnswer: (choice: "same" | "fix" | "new" | "replace" | "back" | "close") => void;
}) {
  const t = useT();
  return (
    <Dialog open={prompt !== null} onOpenChange={(open) => { if (!open && !busy) onAnswer("close"); }}>
      <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()}>
        {prompt && !prompt.confirmReplace && (
          <>
            <DialogHeader>
              <DialogTitle>{t("dup.title")}</DialogTitle>
              <DialogDescription>{t("dup.body", { name: prompt.name, date: prompt.date })}</DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-2">
              <Button type="button" disabled={busy} onClick={() => onAnswer("same")}>{t("dup.same")}</Button>
              <Button type="button" variant="outline" disabled={busy} onClick={() => onAnswer("fix")}>{t("dup.fix")}</Button>
              <Button type="button" variant="outline" disabled={busy} onClick={() => onAnswer("new")}>{t("dup.new")}</Button>
            </div>
          </>
        )}
        {prompt && prompt.confirmReplace && (
          <>
            <DialogHeader>
              <DialogTitle>{t("dup.replaceTitle")}</DialogTitle>
              <DialogDescription>{t("dup.replaceBody", { date: prompt.date })}</DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" disabled={busy} onClick={() => onAnswer("back")}>{t("dup.back")}</Button>
              <Button type="button" disabled={busy} onClick={() => onAnswer("replace")}>{t("dup.replaceConfirm")}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AlreadyAppliedDone({ outcome }: { outcome: "same" | "updated" }) {
  const t = useT();
  return (
    <div className="text-center py-12 px-6">
      <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-2xl font-semibold">
        ✓
      </div>
      <h1 className="text-xl font-semibold text-slate-800 mt-4">
        {t(outcome === "same" ? "dup.sameDoneTitle" : "dup.updatedTitle")}
      </h1>
      <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
        {t(outcome === "same" ? "dup.sameDoneBody" : "dup.updatedBody")}
      </p>
    </div>
  );
}

function Confirmation({ id, onReset }: { id: string; onReset?: () => void }) {
  const t = useT();
  return (
    <div className="text-center py-12 px-6">
      <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-2xl font-semibold">
        ✓
      </div>
      <h1 className="text-xl font-semibold text-slate-800 mt-4">
        {t("done.title")}
      </h1>
      <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
        {t("done.body")}
      </p>
      <p className="text-xs text-slate-400 mt-3 font-mono">{t("done.reference", { id })}</p>
      {onReset && (
        <Button type="button" variant="link" onClick={onReset} className="mt-6">
          {t("done.another")}
        </Button>
      )}
    </div>
  );
}
